import { useCallback } from 'react';
import { useNavigate } from 'react-router';

import type { IConversation } from 'src/api/landlord-messages-api';
import { type IConversationMatchWithChannel } from 'src/pages/landlord/conversations/context/types';
import { type ILead } from 'src/services/api';
import {usePersistentState} from 'src/services/storage/use-persistent-state';
import { getLink } from 'src/pages/landlord/conversations/context/hooks/use-open-conversation';

/**
 * Hook specifically for ConversationsSearchPage to handle opening conversations
 * from search results with proper navigation support.
 */
export const useOpenConversationSearch = (
    leadsByUuid: Map<string, ILead>,
    searchConversationsForNavigation: (IConversation | IConversationMatchWithChannel)[]
) => {
    const navigate = useNavigate();

    const [, saveConversationsStorage] = usePersistentState(
        'conversations',
        [] as IConversation[],
        (value): value is IConversation[] => Array.isArray(value)
    );

    return useCallback(async (conversation: IConversation | IConversationMatchWithChannel, backPath: string) => {
        const lead = leadsByUuid.get(conversation.lead_uuid);
        if (!lead) {
            console.warn('Lead not found for conversation, refusing to open conversation', conversation);
            return;
        }

        const showNavigation = searchConversationsForNavigation.length > 0;
        const conversationsToSave = searchConversationsForNavigation;

        if (showNavigation && conversationsToSave.length > 0) {
            await saveConversationsStorage(conversationsToSave as IConversation[]);
        }

        navigate(getLink(conversation, lead, backPath, showNavigation));
    }, [leadsByUuid, searchConversationsForNavigation, navigate, saveConversationsStorage]);
};
